/**
 * Unified NodeIcon component for displaying n8n node icons
 *
 * Priority-based resolution:
 * 1. Simple Icons CDN (for popular third-party integrations with polished icons)
 * 2. n8n Native SVGs (from GitHub raw CDN for nodes with iconUrl)
 * 3. Font Awesome (for nodes with fa: icons)
 * 4. Letter fallback (first letter of display name)
 */

import { useState, useEffect, createElement } from 'react';
import { getNodeEntry } from '@n8n-builder/n8n-schema';
import { getIntegrationIcon } from '../utils/integrationIcons';
import { FA_ICON_MAP, getN8nIconUrl, getFaIconColor, getFaIconBgColor } from '../utils/nodeIcons';

// Cube icon for unknown nodes
function CubeIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

interface NodeIconProps {
  /** Full node type, e.g., "n8n-nodes-base.slack" */
  nodeType: string;
  /** Size in pixels (default: 24) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

export function NodeIcon({ nodeType, size = 24, className = '' }: NodeIconProps) {
  const [iconSource, setIconSource] = useState<'simple' | 'n8n' | 'fa' | 'letter'>('letter');

  // Get icon info from various sources
  const simpleIconInfo = getIntegrationIcon(nodeType);
  const nodeEntry = getNodeEntry(nodeType);
  const n8nIconInfo = nodeEntry?.icon;
  const displayName = nodeEntry?.displayName || simpleIconInfo?.name || nodeType.split('.').pop() || 'Node';

  // Determine initial icon source when nodeType changes
  useEffect(() => {
    // Determine which icon source to use
    if (simpleIconInfo?.slug) {
      setIconSource('simple');
    } else if (n8nIconInfo?.type === 'url') {
      setIconSource('n8n');
    } else if (n8nIconInfo?.type === 'fa' && FA_ICON_MAP[n8nIconInfo.value]) {
      setIconSource('fa');
    } else if (simpleIconInfo) {
      // Has integration info but no slug - use letter with colors from integrationIcons
      setIconSource('letter');
    } else {
      setIconSource('letter');
    }
  }, [nodeType, simpleIconInfo?.slug, n8nIconInfo?.type, n8nIconInfo?.value]);

  // Handle image load error
  const handleError = () => {
    // Fallback to next option
    if (iconSource === 'simple' && n8nIconInfo?.type === 'url') {
      setIconSource('n8n');
    } else if (iconSource === 'simple' && n8nIconInfo?.type === 'fa' && FA_ICON_MAP[n8nIconInfo.value]) {
      setIconSource('fa');
    } else {
      setIconSource('letter');
    }
  };

  const sizeStyle = { width: size, height: size };
  const iconSize = Math.round(size * 0.6);

  // Simple Icons CDN
  if (iconSource === 'simple' && simpleIconInfo?.slug) {
    const iconUrl = `https://cdn.simpleicons.org/${simpleIconInfo.slug}/${simpleIconInfo.color.replace('#', '')}`;
    return (
      <div
        className={`flex items-center justify-center rounded-md p-1 ${className}`}
        style={{ ...sizeStyle, backgroundColor: simpleIconInfo.bgColor }}
        title={displayName}
      >
        <img
          src={iconUrl}
          alt={displayName}
          className="h-full w-full"
          onError={handleError}
        />
      </div>
    );
  }

  // n8n Native SVGs
  if (iconSource === 'n8n' && n8nIconInfo?.type === 'url') {
    const iconUrl = getN8nIconUrl(n8nIconInfo.value);
    return (
      <div
        className={`flex items-center justify-center rounded-md p-1 ${className}`}
        style={{ ...sizeStyle, backgroundColor: 'rgb(var(--color-gray-100))' }}
        title={displayName}
      >
        <img
          src={iconUrl}
          alt={displayName}
          className="h-full w-full object-contain"
          onError={handleError}
        />
      </div>
    );
  }

  // Font Awesome icons
  if (iconSource === 'fa' && n8nIconInfo?.type === 'fa') {
    const IconComponent = FA_ICON_MAP[n8nIconInfo.value];
    if (IconComponent) {
      const color = getFaIconColor(n8nIconInfo.color);
      const bgColor = getFaIconBgColor(n8nIconInfo.color);
      return (
        <div
          className={`flex items-center justify-center rounded-md ${className}`}
          style={{ ...sizeStyle, backgroundColor: bgColor }}
          title={displayName}
        >
          {createElement(IconComponent, {
            style: { width: iconSize, height: iconSize, color },
          })}
        </div>
      );
    }
  }

  // Letter fallback
  if (simpleIconInfo) {
    // Use colors from integrationIcons if available
    return (
      <div
        className={`flex items-center justify-center rounded-md text-xs font-semibold ${className}`}
        style={{
          ...sizeStyle,
          backgroundColor: simpleIconInfo.bgColor,
          color: simpleIconInfo.color,
          fontSize: Math.round(size * 0.45),
        }}
        title={displayName}
      >
        {displayName.charAt(0).toUpperCase()}
      </div>
    );
  }

  // Generic cube icon for completely unknown nodes
  return (
    <div
      className={`flex items-center justify-center rounded-md bg-gray-100 ${className}`}
      style={sizeStyle}
      title={displayName}
    >
      <CubeIcon className="text-gray-500" style={{ width: iconSize, height: iconSize }} />
    </div>
  );
}
